import { action, computed, observable } from "mobx";

import { DateTime } from "@bps/date-time";
import { confirm } from "@bps/fluent-ui";
import {
  AttendeeTypeEnum,
  CalendarEventType
} from "@libs/gateways/booking/BookingGateway.dtos.ts";
import { maybePromiseObservable } from "@libs/utils/promise-observable/promise-observable.utils.ts";
import { BookingStore } from "@stores/booking/BookingStore.ts";
import { WaitingListItemModel } from "@stores/booking/models/WaitingListModel.ts";

import { WaitingListFilterInternal } from "../components/WaitingListDetailFilter.tsx";
import { WaitingListItem } from "../WaitingListScreen.types.ts";

export class WaitingListScreenHelper {
  constructor(private booking: BookingStore) {}

  public waitingListResults = maybePromiseObservable<WaitingListItemModel[]>();

  @observable
  public closeDialog: boolean = true;

  @observable
  public itemToEdit: WaitingListItem | undefined = undefined;

  @computed
  public get items() {
    return (
      this.waitingListResults.value?.filter(
        x => DateTime.fromISO(x.until) >= DateTime.today()
      ) ?? []
    );
  }

  @action
  setCloseDialog = (value: boolean) => {
    this.closeDialog = value;
  };

  @action
  setItemToEdit = (value: WaitingListItem | undefined) => {
    this.itemToEdit = value;
  };

  public editWaitingList = async (item: WaitingListItem) => {
    if (!!item?.calenderEventId) {
      if (!this.booking.calendarEventsMap.has(item.calenderEventId)) {
        await this.booking.getCalendarEvent(item.calenderEventId, {
          ignoreCache: true
        });
      }
      this.booking.ui.showCalendarEventDialog({
        id: item.calenderEventId,
        type: CalendarEventType.Appointment
      });
    } else {
      this.setCloseDialog(false);
      this.setItemToEdit(item);
    }
  };

  public removeWaitingList = async (item: WaitingListItem) => {
    const contactId = item.attendees?.find(
      x => x.type === AttendeeTypeEnum.contact
    )?.attendeeId;

    const contact =
      contactId && this.booking.practice.contactsMap.get(contactId);

    if (
      contact &&
      (await confirm({
        confirmButtonProps: {
          text: "Confirm"
        },
        cancelButtonProps: {
          text: "Cancel"
        },
        dialogContentProps: {
          title: "Remove from waiting list",
          subText: `Are you sure you want to remove ${contact.name} from the waiting list?`
        }
      }))
    ) {
      await this.booking.deleteWaitingListRecord(item.id);
    }
  };

  // Filtering by provider really ought to be done on the backend
  public filterItemsWithAnyProvider = (
    items: WaitingListItemModel[],
    filter: WaitingListFilterInternal
  ) => {
    if (!filter.providerId) {
      return items;
    }
    return items.filter(item => {
      return (
        item.attendees.some(i => i.attendeeId === filter.providerId) ||
        item.anyProvider ||
        (item.anyLocation &&
          filter.anyProviderOrLocation?.includes("anyLocation"))
      );
    });
  };

  public fetchWaitingListItems = (filter: WaitingListFilterInternal) => {
    this.waitingListResults.set(
      this.booking
        .getWaitingListRecordsFilteredValues(filter)
        .then(result => this.filterItemsWithAnyProvider(result, filter))
    );
  };
}
